import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  ClipboardList,
  Receipt,
  Banknote,
  FileText,
  FileSpreadsheet,
  Settings,
  ArrowLeft,
  X,
  User,
} from 'lucide-react';
import { useSiteStudy } from '../contexts/SiteStudyContext';

interface SiteStudySidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

const formatPhysicianName = (physician: { lastName: string; firstName: string; middleName?: string | null; suffix?: string | null; credential?: string | null }): string => {
  let name = `${physician.firstName} ${physician.lastName}`;
  if (physician.suffix) name += ` ${physician.suffix}`;
  if (physician.credential) name += `, ${physician.credential}`;
  return name;
};

const SiteStudySidebar = ({ sidebarOpen, setSidebarOpen }: SiteStudySidebarProps) => {
  const location = useLocation();
  const { study, siteOrganization, primaryAlias, basePath, loading, investigators, principalInvestigator } = useSiteStudy();

  const navigation = [
    {
      name: 'Summary',
      href: basePath,
      icon: LayoutDashboard,
      exact: true,
    },
    {
      name: 'Activities',
      href: `${basePath}/activities`,
      icon: ClipboardList,
    },
    {
      name: 'Site Costs',
      href: `${basePath}/site-costs`,
      icon: Receipt,
    },
    {
      name: 'Invoices',
      href: `${basePath}/invoices`,
      icon: FileSpreadsheet,
    },
    {
      name: 'Payments',
      href: `${basePath}/payments`,
      icon: Banknote,
    },
    {
      name: 'Contracts',
      href: `${basePath}/contracts`,
      icon: FileText,
    },
    {
      name: 'Settings',
      href: `${basePath}/settings`,
      icon: Settings,
    },
  ];

  const isCurrent = (item: typeof navigation[0]) => {
    if (item.exact) {
      return location.pathname === item.href;
    }
    return location.pathname.startsWith(item.href);
  };

  return (
    <>
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-600 bg-opacity-75 lg:hidden z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col`}
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-between h-16 px-6 bg-slate-900">
          <div className="flex items-center space-x-2">
            <LayoutDashboard className="h-8 w-8 text-white" />
            <span className="text-xl font-bold text-white">Trio Admin</span>
          </div>
          <button
            className="lg:hidden text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Scope indicator */}
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
          <Link
            to="/studies"
            className="flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium mb-2"
            onClick={() => setSidebarOpen(false)}
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            All Studies
          </Link>
          {loading ? (
            <div className="text-xs text-slate-400">Loading...</div>
          ) : (
            <>
              <div className="text-sm font-semibold text-slate-900 truncate">
                {primaryAlias || study?.protocolId || 'Study'}
              </div>
              {primaryAlias && study?.protocolId && primaryAlias !== study.protocolId && (
                <div className="text-[11px] text-slate-400 truncate">
                  {study.protocolId}
                </div>
              )}
              <div className="text-xs text-slate-500 truncate">
                {siteOrganization?.name || 'Site'}
              </div>
              {/* Principal Investigator */}
              {investigators.length > 0 ? (
                investigators.map(ssi => (
                  <div key={ssi.id} className="flex items-center gap-1.5 mt-1.5">
                    <User className="w-3 h-3 text-slate-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-slate-700 truncate">
                        {ssi.physician ? formatPhysicianName(ssi.physician) : ssi.sourcePiName || 'Unknown PI'}
                      </div>
                      {ssi.physician && (
                        <div className="text-[10px] text-slate-400 truncate">
                          NPI: {ssi.physician.npi}
                          {ssi.physician.primarySpecialty && ` · ${ssi.physician.primarySpecialty}`}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : principalInvestigator ? (
                <div className="flex items-center gap-1.5 mt-1.5">
                  <User className="w-3 h-3 text-slate-400 flex-shrink-0" />
                  <div className="text-xs text-slate-500 truncate">{principalInvestigator}</div>
                </div>
              ) : null}
            </>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 mt-4 px-4 overflow-y-auto">
          <ul className="space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const current = isCurrent(item);
              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={`flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                      current
                        ? 'bg-blue-50 text-blue-700 border-r-4 border-blue-700'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon
                      className={`mr-3 h-5 w-5 ${
                        current ? 'text-blue-700' : 'text-slate-400'
                      }`}
                    />
                    {item.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </>
  );
};

export default SiteStudySidebar;
